'use client'

import { useState } from 'react'

import { useRecaptchaV3 } from '@/hooks/useRecaptchaV3'
import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

interface ErrorResponse {
  errors?: string[]
  message?: string
  error?: string
}

type VerifyChannel = 'sms' | 'email'

interface SendCodeResult {
  success: boolean
  channel?: VerifyChannel
}

export const useEmailVerify = (): {
  loading: boolean
  error: string | null
  channel: VerifyChannel | null
  sendCode: (email: string) => Promise<SendCodeResult>
} => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [channel, setChannel] = useState<VerifyChannel | null>(null)
  const { executeRecaptcha } = useRecaptchaV3()

  const sendCode = async (email: string): Promise<SendCodeResult> => {
    setError(null)
    setLoading(true)
    try {
      // Invisible reCAPTCHA v3 token (action must match the backend guard's
      // 'mobile_verification'). A null token is omitted; the backend then
      // rejects the send rather than letting a bot farm codes (GSE-12291).
      const recaptchaToken = await executeRecaptcha('mobile_verification')
      const url = getV5ApiUrl('/users/mobile_verify/email_verify')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
        },
        body: JSON.stringify({
          email,
          brand_id: brand.key,
          recaptcha_token: recaptchaToken ?? undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const errorData = data as ErrorResponse
        throw new Error(
          errorData.message ||
            errorData.errors?.[0] ||
            'Failed to send verification code. Please try again.',
        )
      }

      const responseChannel: VerifyChannel = data.channel === 'email' ? 'email' : 'sms'
      setChannel(responseChannel)
      return { success: true, channel: responseChannel }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send verification code. Please try again.',
      )
      return { success: false }
    } finally {
      setLoading(false)
    }
  }

  return { loading, error, channel, sendCode }
}
